import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useReactFlow } from '@xyflow/react';

// Libs
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import ConfirmationModal from './ConfirmationModal';
import InputModal from './InputModal';

// Utils
import { exportToGeniaWeb, importFromGeniaWeb, validateImportedData, createBackup } from "~/utils/fileUtils";
import { sincronizarVisibilidadeAvatares } from "~/functions/nodeFunctions";

// Contexts
import { useToast } from "~/contexts/ToastContext";
import { useUnsavedChangesContext } from "~/contexts/UnsavedChangesContext";
import { useConfirmationModal } from '~/hooks/useConfirmationModal';
import { useTheme } from "~/contexts/ThemeContext";
import { useDevMode } from "~/contexts/DevModeContext";

// Store
import { myStore } from "~/App";
import { setNodes } from "~/actions/nodes";
import { setEdges } from "~/actions/edges";
import { setVariaveis } from "~/actions/variaveisActions";
import { setAvatars } from "~/actions/avatarsActions";
import { setRects } from "~/actions/rects";

// Styles
import "~/styles/Header.css"

export const Header = () => {
    const [showFileMenu, setShowFileMenu] = useState(false);
    const [showFlowMenu, setShowFlowMenu] = useState(false);
    const [showStageMenu, setShowStageMenu] = useState(false);
    const [showAIMenu, setShowAIMenu] = useState(false);
    const [showStageConfigModal, setShowStageConfigModal] = useState(false);
    const [theaterModeEnabled, setTheaterModeEnabled] = useState(true); // Ativo por padrão
    const fileInputRef = useRef(null);
    
    // Estado do modo desenvolvedor
    const [clickCount, setClickCount] = useState(0);
    const clickTimeoutRef = useRef(null);
    
    // Refs para os dropdowns
    const fileMenuRef = useRef(null);
    const flowMenuRef = useRef(null);
    const stageMenuRef = useRef(null);
    const aiMenuRef = useRef(null);
    
    const { showSuccess, showError, showWarning } = useToast();
    const { fitView } = useReactFlow();
    const { currentTheme, toggleTheme } = useTheme();
    const { devMode: globalDevMode, setDevMode: setGlobalDevMode } = useDevMode();
    const { 
        modalState, 
        inputModalState,
        showConfirm, 
        showInput,
        showSuccess: showSuccessModal, 
        showError: showErrorModal,
    } = useConfirmationModal();
    
    // Context para mudanças não salvas
    const { hasUnsavedChanges, markAsSaved } = useUnsavedChangesContext();
    
    // Detectar triple click no título GenIA
    const handleGeniaClick = () => {
        setClickCount(prev => prev + 1);
        
        // Limpar timeout anterior
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
        }
        
        // Configurar novo timeout
        clickTimeoutRef.current = setTimeout(() => {
            if (clickCount + 1 >= 3) {
                const newDevMode = !globalDevMode;
                setGlobalDevMode(newDevMode);
                showSuccess(newDevMode ? 'Modo desenvolvedor ativado' : 'Modo desenvolvedor desativado');
            }
            setClickCount(0);
        }, 500); // 500ms para detectar triple click
    };
    
    // Cleanup do timeout
    useEffect(() => {
        return () => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, []);

    // Carregar configuração do modo teatro do localStorage
    useEffect(() => {
        const savedTheaterMode = localStorage.getItem('theaterModeEnabled');
        if (savedTheaterMode !== null) {
            setTheaterModeEnabled(JSON.parse(savedTheaterMode));
        }
    }, []);

    // Detectar cliques fora dos dropdowns para fechá-los
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (fileMenuRef.current && !fileMenuRef.current.contains(event.target)) {
                setShowFileMenu(false);
            }
            if (flowMenuRef.current && !flowMenuRef.current.contains(event.target)) {
                setShowFlowMenu(false);
            }
            if (stageMenuRef.current && !stageMenuRef.current.contains(event.target)) {
                setShowStageMenu(false);
            }
            if (aiMenuRef.current && !aiMenuRef.current.contains(event.target)) {
                setShowAIMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Salvar configuração do modo teatro no localStorage
    const handleTheaterModeToggle = (enabled) => {
        setTheaterModeEnabled(enabled);
        localStorage.setItem('theaterModeEnabled', JSON.stringify(enabled));
        
        // Disparar evento para que o componente Stage saiba da mudança
        window.dispatchEvent(new CustomEvent('theaterModeConfigChanged', { 
            detail: { enabled } 
        }));
        
        if (enabled) {
            showSuccess("Modo Teatro ativado! Será usado automaticamente durante execuções.");
        } else {
            showSuccess("Modo Teatro desativado.");
        }
    };

    const handleStageConfig = () => {
        setShowStageConfigModal(true);
        setShowStageMenu(false);
    };

    const handleValidateFlow = () => {
        // Por enquanto apenas mostra uma mensagem, mas pode ser expandido
        showSuccess("Funcionalidade de validação será implementada em breve!");
        setShowFlowMenu(false);
    };

    const handleAIConfig = () => {
        // Por enquanto apenas mostra uma mensagem, mas pode ser expandido  
        showSuccess("Configurações de IA serão implementadas em breve!");
        setShowAIMenu(false);
    };

    // Funções para toggle dos dropdowns
    const toggleFileMenu = () => {
        setShowFileMenu(!showFileMenu);
        // Fechar outros dropdowns
        setShowFlowMenu(false);
        setShowStageMenu(false);
        setShowAIMenu(false);
    };

    const toggleFlowMenu = () => {
        setShowFlowMenu(!showFlowMenu);
        // Fechar outros dropdowns
        setShowFileMenu(false);
        setShowStageMenu(false);
        setShowAIMenu(false);
    };

    const toggleStageMenu = () => {
        setShowStageMenu(!showStageMenu);
        // Fechar outros dropdowns
        setShowFileMenu(false);
        setShowFlowMenu(false);
        setShowAIMenu(false);
    };

    const toggleAIMenu = () => {
        setShowAIMenu(!showAIMenu);
        // Fechar outros dropdowns
        setShowFileMenu(false);
        setShowFlowMenu(false);
        setShowStageMenu(false);
    };

    // Get current state from Redux
    const nodes = useSelector((state) => state.nodes.nodes);
    const edges = useSelector((state) => state.edges.edges);
    const variables = useSelector((state) => state.variaveis);
    const avatars = useSelector((state) => state.avatars);
    const rects = useSelector((state) => state.rects.rects);

    // Função para organizar nós em fluxo
    const handleOrganizeNodes = () => {
        if (nodes.length === 0) {
            showWarning("Não há nós para organizar!");
            return;
        }

        try {
            // Configurações de layout VERTICAL (um embaixo do outro)
            const SPACING_X = 250;     // Espaçamento horizontal para ramificações
            const START_X = 400;       // Posição X central
            const START_Y = 100;       // Posição Y inicial
            const NODE_PADDING = 50;   // Padding extra entre nós (incluindo espaço para setas)

            // Função para obter o tamanho real de um nó usando measured ou calculado
            const getNodeSize = (nodeId) => {
                const node = nodes.find(n => n.id === nodeId);
                if (!node) return { width: 200, height: 100 };

                // Priorizar dimensões measured (mais precisas)
                if (node.measured?.width && node.measured?.height) {
                    return {
                        width: node.measured.width,
                        height: node.measured.height
                    };
                }

                // Fallback: calcular baseado no tipo se não houver measured
                return calculateNodeSizeFallback(nodeId);
            };

            // Função de fallback para calcular tamanho quando measured não está disponível
            const calculateNodeSizeFallback = (nodeId) => {
                const node = nodes.find(n => n.id === nodeId);
                if (!node) return { width: 200, height: 100 };

                // Tamanhos base mais realistas por tipo de nó
                const nodeSizes = {
                    'inicioNode': { width: 140, height: 80 },
                    'fimNode': { width: 140, height: 80 },
                    'decidirNode': { width: 220, height: 140 },
                    'interagirAvatarNode': { width: 300, height: 250 },
                    'lerNode': { width: 240, height: 120 },
                    'mostrarNode': { width: 240, height: 120 },
                    'atribuirNode': { width: 220, height: 100 },
                    'esperarNode': { width: 180, height: 90 },
                    'avatarPosicionarNode': { width: 240, height: 130 },
                    'avatarVisivelNode': { width: 240, height: 110 },
                    'default': { width: 200, height: 100 }
                };

                const baseSize = nodeSizes[node.type] || nodeSizes.default;
                let adjustedHeight = baseSize.height;
                let adjustedWidth = baseSize.width;

                // Ajustar baseado no conteúdo do nó
                if (node.data?.label) {
                    const labelLength = node.data.label.length;
                    // Cada caractere ocupa aproximadamente 8px
                    const estimatedLabelWidth = labelLength * 8;
                    adjustedWidth = Math.max(adjustedWidth, estimatedLabelWidth + 40);
                }

                // Considerações específicas por tipo
                if (node.type === 'interagirAvatarNode') {
                    // Nós de interação são muito maiores devido aos controles
                    adjustedHeight = Math.max(250, adjustedHeight);
                    adjustedWidth = Math.max(300, adjustedWidth);
                }

                if (node.type === 'decidirNode') {
                    // Nós de decisão têm campos de condição
                    adjustedHeight = Math.max(140, adjustedHeight);
                    adjustedWidth = Math.max(220, adjustedWidth);
                }

                return {
                    width: adjustedWidth,
                    height: adjustedHeight
                };
            };

            // Encontrar nó de início
            const startNode = nodes.find(node =>
                node.type === 'inicioNode' ||
                node.data.label?.toLowerCase().includes('inicio') ||
                node.id === "1"
            );

            // Função para organizar nós em sequência vertical centralizada
            const organizeVertically = () => {
                const organizedNodes = [...nodes];
                const processedNodes = new Set();
                const nodePositions = new Map();
                const nodeSizes = new Map();
                let currentY = START_Y;

                // Calcular tamanhos reais de todos os nós usando measured quando disponível
                organizedNodes.forEach(node => {
                    const size = getNodeSize(node.id);
                    nodeSizes.set(node.id, size);
                    console.log(`Nó ${node.id} (${node.type}): ${size.width}x${size.height} ${node.measured ? '[measured]' : '[calculated]'}`);
                });

                // Função para seguir o fluxo principal verticalmente
                const followMainFlow = (nodeId, centerX = START_X) => {
                    if (processedNodes.has(nodeId)) return;

                    const node = organizedNodes.find(n => n.id === nodeId);
                    if (!node) return;

                    const nodeSize = nodeSizes.get(nodeId);

                    // Posicionar o nó centralizado horizontalmente
                    const nodeX = centerX - (nodeSize.width / 2);

                    nodePositions.set(nodeId, {
                        x: nodeX,
                        y: currentY
                    });

                    processedNodes.add(nodeId);

                    // Avançar Y baseado na altura REAL do nó atual + padding (que inclui espaço para setas)
                    const nextY = currentY + nodeSize.height + NODE_PADDING;
                    currentY = nextY;

                    // Encontrar próximo nó no fluxo
                    const outgoingEdges = edges.filter(edge => edge.source === nodeId);

                    if (outgoingEdges.length === 1) {
                        // Fluxo linear - continuar na mesma linha vertical centralizada
                        followMainFlow(outgoingEdges[0].target, centerX);
                    } else if (outgoingEdges.length > 1) {
                        // Ramificação - distribuir horizontalmente mas depois reconverger
                        const savedY = currentY;
                        let maxY = currentY;

                        // Para nós de decisão, organizar verdadeiro/falso de forma especial
                        if (node.type === 'decidirNode' || node.data.label === 'Decidir') {
                            const trueEdge = outgoingEdges.find(edge => edge.sourceHandle === 'verdadeiro');
                            const falseEdge = outgoingEdges.find(edge => edge.sourceHandle === 'falso');

                            if (trueEdge) {
                                currentY = savedY;
                                followMainFlow(trueEdge.target, centerX + SPACING_X);
                                maxY = Math.max(maxY, currentY);
                            }

                            if (falseEdge) {
                                currentY = savedY;
                                followMainFlow(falseEdge.target, centerX - SPACING_X);
                                maxY = Math.max(maxY, currentY);
                            }
                        } else {
                            // Outras ramificações
                            outgoingEdges.forEach((edge, index) => {
                                const totalBranches = outgoingEdges.length;
                                const branchIndex = index - Math.floor(totalBranches / 2);
                                const branchCenterX = centerX + (branchIndex * SPACING_X);

                                currentY = savedY;
                                followMainFlow(edge.target, branchCenterX);
                                maxY = Math.max(maxY, currentY);
                            });
                        }

                        // Usar o Y máximo para continuar
                        currentY = maxY;
                    }
                };

                // Começar pelo nó de início ou primeiro nó disponível
                const startNodeId = startNode ? startNode.id : nodes[0].id;
                followMainFlow(startNodeId);

                // Posicionar nós órfãos (não conectados) à direita
                let orphanY = START_Y;
                organizedNodes.forEach(node => {
                    if (!processedNodes.has(node.id)) {
                        const nodeSize = nodeSizes.get(node.id);
                        const orphanCenterX = START_X + (SPACING_X * 2);
                        const orphanX = orphanCenterX - (nodeSize.width / 2);

                        nodePositions.set(node.id, {
                            x: orphanX,
                            y: orphanY
                        });
                        orphanY += nodeSize.height + NODE_PADDING;
                    }
                });

                // Aplicar as posições
                const updatedNodes = organizedNodes.map(node => ({
                    ...node,
                    position: nodePositions.get(node.id) || node.position
                }));

                return updatedNodes;
            };

            // Organizar e atualizar
            const organizedNodes = organizeVertically();
            myStore.dispatch(setNodes(organizedNodes));

            showSuccess(`${nodes.length} nós organizados em fluxo vertical centralizado!`);

            // Ajustar a visualização para mostrar todos os nós organizados
            setTimeout(() => {
                fitView({ padding: 0.1, duration: 500 });
            }, 100);

            setShowFlowMenu(false);

        } catch (error) {
            console.error('Erro ao organizar nós:', error);
            showError("Erro ao organizar os nós: " + error.message);
        }
    };

    const handleExport = async () => {
        const flowData = {
            nodes,
            edges,
            variables,
            avatars,
            rects
        };

        // Usando um modal personalizado para pedir o nome do arquivo
        const filename = await showInput({
            title: "Exportar Fluxograma",
            message: "Digite o nome do arquivo:",
            placeholder: "Ex: meu_fluxograma",
            defaultValue: "meu_fluxograma",
            type: "info"
        });
        
        if (filename) {
            const result = exportToGeniaWeb(flowData, filename);
            if (result.success) {
                // Marca como salvo após exportação bem-sucedida
                markAsSaved();
                await showSuccessModal(result.message);
            } else {
                await showErrorModal("Erro: " + result.message);
            }
        }
        setShowFileMenu(false);
    };

    // Listener para atalho Ctrl+S
    useEffect(() => {
        const handleTriggerExport = (event) => {
            console.log('🎯 Evento triggerExport recebido no Header');
            if (event.detail.exportType === 'normal') {
                handleExport();
            }
        };

        window.addEventListener('triggerExport', handleTriggerExport);
        return () => window.removeEventListener('triggerExport', handleTriggerExport);
    }, [handleExport]);

    const handleQuickExport = () => {
        const flowData = {
            nodes,
            edges,
            variables,
            avatars,
            rects
        };

        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `fluxograma_${timestamp}`;

        const result = exportToGeniaWeb(flowData, filename);
        if (result.success) {
            // Marca como salvo após exportação bem-sucedida
            markAsSaved();
            showSuccess(`Arquivo exportado como: ${filename}.geniaweb`);
        } else {
            showError("Erro: " + result.message);
        }
        setShowFileMenu(false);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
        setShowFileMenu(false);
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            // Create backup before importing
            const currentState = { nodes, edges, variables, avatars, rects };
            createBackup(currentState);

            // Import file
            const result = await importFromGeniaWeb(file);

            if (result.success) {
                // Validate imported data
                const validation = validateImportedData(result.data);

                if (!validation.isValid) {
                    await showErrorModal("Arquivo inválido:\n" + validation.errors.join("\n"));
                    return;
                }

                if (validation.warnings.length > 0) {
                    const proceed = await showConfirm("Avisos encontrados:\n" + validation.warnings.join("\n") + "\n\nDeseja continuar?");
                    if (!proceed) return;
                    showWarning("Arquivo importado com avisos. Verifique os dados.");
                }

                // Confirm import
                const confirmImport = await showConfirm("Importar este fluxograma irá substituir o atual. Deseja continuar?");
                if (!confirmImport) return;

                // Sanitize and update Redux store with imported data
                const sanitizedNodes = Array.isArray(result.data.nodes) ? result.data.nodes : [];
                const sanitizedEdges = Array.isArray(result.data.edges) ? result.data.edges : [];
                const sanitizedVariables = Array.isArray(result.data.variables) ? result.data.variables : [];
                const sanitizedAvatars = Array.isArray(result.data.avatars) ? result.data.avatars : [];
                const sanitizedRects = Array.isArray(result.data.rects) ? result.data.rects : [];

                myStore.dispatch(setNodes(sanitizedNodes));
                myStore.dispatch(setEdges(sanitizedEdges));
                myStore.dispatch(setVariaveis(sanitizedVariables));
                myStore.dispatch(setAvatars(sanitizedAvatars));
                myStore.dispatch(setRects(sanitizedRects));

                // Sincroniza a visibilidade dos avatares após importação
                sincronizarVisibilidadeAvatares();

                // Centraliza o fluxograma automaticamente após importação
                setTimeout(() => {
                    fitView({ 
                        padding: 0.1, 
                        duration: 800,
                        minZoom: 0.5,
                        maxZoom: 1.5
                    });
                }, 100);

                // Marca como salvo após importação bem-sucedida
                markAsSaved();
                await showSuccessModal(result.message);
            }
        } catch (error) {
            await showErrorModal("Erro ao importar arquivo: " + error.message);
        }

        // Reset file input
        event.target.value = '';
    };

    const handleNewFlow = async () => {
        let confirmed = false;
        
        // Verificar se há mudanças não salvas antes de criar novo fluxograma
        if (hasUnsavedChanges) {
            confirmed = await showConfirm(
                "Você tem alterações não salvas no diagrama atual. Criar um novo fluxograma irá perder essas alterações. Deseja continuar?"
            );
        } else {
            confirmed = await showConfirm("Criar um novo fluxograma irá limpar o atual. Deseja continuar?");
        }

        if (!confirmed) {
            setShowFileMenu(false);
            return;
        }

        // Reset to initial state
        myStore.dispatch(setNodes([
            { id: '1', data: { label: 'Início' }, position: { x: 0, y: 0 }, type: 'Inicio' },
            { id: '2', data: { label: 'Fim' }, position: { x: 0, y: 300 }, type: 'Fim' }
        ]));
        myStore.dispatch(setEdges([]));
        myStore.dispatch(setVariaveis([]));
        myStore.dispatch(setAvatars([]));
        myStore.dispatch(setRects([]));
        
        // Marca como salvo após criar novo fluxograma
        markAsSaved();
        setShowFileMenu(false);
    };

    return (
        <div className="header">
            <ul className="menus-principais">
                <li className="menu-principal" onClick={handleGeniaClick} style={{ cursor: 'pointer' }}>
                    <FontAwesomeIcon icon="fa-solid fa-house" />
                    <span>GenIA</span>
                    {globalDevMode && <span style={{ color: '#ff9800', fontSize: '10px', marginLeft: '4px' }}>DEV</span>}
                </li>
                <li
                    className="menu-principal"
                    onClick={toggleFileMenu}
                    ref={fileMenuRef}
                >
                    Arquivo {hasUnsavedChanges && <span className="unsaved-indicator">●</span>} <FontAwesomeIcon icon="fa-solid fa-caret-down" />
                    {showFileMenu && (
                        <ul className="submenu">
                            <li onClick={handleNewFlow}>
                                <span><FontAwesomeIcon icon="fa-solid fa-file" /> Novo Fluxograma</span>
                            </li>
                            <li onClick={handleImportClick}>
                                <span><FontAwesomeIcon icon="fa-solid fa-upload" /> Importar (.geniaweb)</span>
                            </li>
                            <li onClick={handleExport}>
                                <span><FontAwesomeIcon icon="fa-solid fa-download" /> Exportar (.geniaweb)</span>
                                {hasUnsavedChanges && <span className="unsaved-marker">*</span>}
                            </li>
                            <li onClick={handleQuickExport}>
                                <span><FontAwesomeIcon icon="fa-solid fa-save" /> Exportação Rápida</span>
                                {hasUnsavedChanges && <span className="unsaved-marker">*</span>}
                            </li>
                        </ul>
                    )}
                </li>
                <li
                    className="menu-principal"
                    onClick={toggleFlowMenu}
                    ref={flowMenuRef}
                >
                    Fluxograma <FontAwesomeIcon icon="fa-solid fa-caret-down" />
                    {showFlowMenu && (
                        <ul className="submenu">
                            <li onClick={handleOrganizeNodes} style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon="fa-solid fa-sitemap" style={{ marginRight: '8px' }} />
                                Organizar Nós
                            </li>
                            <li onClick={handleValidateFlow} style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon="fa-solid fa-check-circle" style={{ marginRight: '8px' }} />
                                Validar Fluxo
                            </li>
                            <a href=""></a>                        </ul>
                    )}
                </li>
                <li
                    className="menu-principal"
                    onClick={toggleStageMenu}
                    ref={stageMenuRef}
                >
                    Palco <FontAwesomeIcon icon="fa-solid fa-caret-down" />
                    {showStageMenu && (
                        <ul className="submenu">
                            <li onClick={handleStageConfig} style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon="fa-solid fa-cog" style={{ marginRight: '8px' }} />
                                Configurações do Palco
                            </li>
                        </ul>
                    )}
                </li>
                <li
                    className="menu-principal"
                    onClick={toggleAIMenu}
                    ref={aiMenuRef}
                >
                    Inteligencia Artificial <FontAwesomeIcon icon="fa-solid fa-caret-down" />
                    {showAIMenu && (
                        <ul className="submenu">
                            <li onClick={handleAIConfig} style={{ cursor: 'pointer' }}>Configurações IA</li>
                        </ul>
                    )}
                </li>
                <li className="menu-principal">
                    <FontAwesomeIcon icon="fa-solid fa-question-circle" />
                    Ajuda e Tutoriais
                </li>
                
                {/* Botão de alternância de tema */}
                <li className="menu-principal theme-toggle-container">
                    <button 
                        className={`theme-toggle-btn ${currentTheme}`}
                        onClick={toggleTheme}
                        title={`Alternar para design ${currentTheme === 'rf' ? 'moderno' : 'clássico'}`}
                    >
                        <FontAwesomeIcon 
                            icon={currentTheme === 'rf' ? 'fa-solid fa-palette' : 'fa-solid fa-desktop'} 
                        />
                        <span className="theme-label">
                            {currentTheme === 'rf' ? 'Moderno' : 'Clássico'}
                        </span>
                    </button>
                </li>
            </ul>

            {/* Hidden file input for import */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".geniaweb"
                onChange={handleImport}
            />

            {/* Modals */}
            <ConfirmationModal
                isOpen={modalState.isOpen}
                title={modalState.title}
                message={modalState.message}
                confirmText={modalState.confirmText}
                cancelText={modalState.cancelText}
                onConfirm={modalState.onConfirm}
                onCancel={modalState.onCancel}
                type={modalState.type}
            />

            <InputModal
                isOpen={inputModalState.isOpen}
                title={inputModalState.title}
                message={inputModalState.message}
                placeholder={inputModalState.placeholder}
                defaultValue={inputModalState.defaultValue}
                confirmText={inputModalState.confirmText}
                cancelText={inputModalState.cancelText}
                onConfirm={inputModalState.onConfirm}
                onCancel={inputModalState.onCancel}
                type={inputModalState.type}
            />

            {/* Modal de Configurações do Palco */}
            {showStageConfigModal && (
                <div className="modal-overlay" onClick={() => setShowStageConfigModal(false)}>
                    <div className="modal-content stage-config-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <FontAwesomeIcon icon="fa-solid fa-cog" style={{ marginRight: '10px' }} />
                                Configurações do Palco
                            </h3>
                            <button 
                                className="modal-close-btn"
                                onClick={() => setShowStageConfigModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="config-section">
                                <div className="config-item">
                                    <div className="config-label">
                                        <FontAwesomeIcon icon="fa-solid fa-tv" style={{ marginRight: '8px' }} />
                                        <strong>Modo Teatro</strong>
                                        <span className="config-description">
                                            Expande o palco para tela cheia durante a execução
                                        </span>
                                    </div>
                                    <div className="config-control">
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={theaterModeEnabled}
                                                onChange={(e) => handleTheaterModeToggle(e.target.checked)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className={`toggle-status ${theaterModeEnabled ? 'enabled' : 'disabled'}`}>
                                            {theaterModeEnabled ? 'Ativado' : 'Desativado'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="config-info">
                                <FontAwesomeIcon icon="fa-solid fa-info-circle" style={{ marginRight: '8px' }} />
                                <small>
                                    Quando ativo, o palco automaticamente entrará em modo teatro durante execuções, 
                                    proporcionando uma experiência imersiva em tela cheia.
                                </small>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowStageConfigModal(false)}
                            >
                                <FontAwesomeIcon icon="fa-solid fa-check" style={{ marginRight: '6px' }} />
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}